/**
 * Custom hook for Calendar state management
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Views } from 'react-big-calendar';
import { calendarAPI, fetchAllPages, CalendarEvent } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { useModal } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { CalendarViewType, ViewMode, EventPeriod } from './types';
import { ALL_EVENT_TYPES, ITEMS_PER_PAGE } from './constants';
import { AUDIT_ACTION_PREFIX, auditActionPath } from './eventLinks';
import { filterEvents, transformEventsForCalendar, groupEventsByMonth, eventsToCsv, downloadCsv } from './utils';
import { useCalendarFilters } from './useCalendarFilters';
import { useView } from '../../contexts/ViewContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export function useCalendar() {
  const confirm = useConfirm();
  const { viewKey, getViewQueryParams, isLoading: viewContextLoading } = useView();
  const { user } = useAuth();
  // CR-6435 / CR-6436: the filter panel's draft / applied state for both
  // views; the applied query params ride on every events request.
  const filters = useCalendarFilters();

  // View states
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [calendarView, setCalendarView] = useState<CalendarViewType>(Views.MONTH as CalendarViewType);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Data states
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [activeEventTypes, setActiveEventTypes] = useState<string[]>(ALL_EVENT_TYPES);
  const [eventPeriod, setEventPeriod] = useState<EventPeriod>('upcoming');

  // List View Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Event Modal State
  const eventModal = useModal<CalendarEvent>();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<CalendarEvent>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Fetch events from API
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const viewParams = getViewQueryParams();
      // CR-5459: opt into the synthetic Approval Expiry / Contract Renewal
      // / QBR rows that the backend derives from SupplierProfile,
      // ContractPrice and matching CalendarEvent titles.
      // Walk pages — the backend clamps page_size, so one request capped
      // the calendar at one page of events.
      const allEvents = await fetchAllPages<CalendarEvent>(
        (page) => calendarAPI.getEvents({ ordering: 'start_datetime', page_size: 100, page, include_synthetic: 'true', ...viewParams, ...filters.queryParams }),
      );
      setEvents(allEvents);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load calendar events'));
    } finally {
      setLoading(false);
    }
  }, [getViewQueryParams, filters.queryParams]);

  // Fetch initial data (and re-fetch when view context or user changes).
  // Waits for ViewContext to resolve so the first events request already
  // carries the Group/Division/Site scope instead of firing unscoped and
  // again scoped.
  useEffect(() => {
    if (viewContextLoading) return;
    // Clear stale data when context changes to prevent showing old data
    setEvents([]);
    fetchEvents();
  }, [viewKey, user?.id, fetchEvents, viewContextLoading]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [eventPeriod, activeEventTypes]);

  const handleEventTypeToggle = useCallback((type: string) => {
    setActiveEventTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }, []);

  const navigate = useNavigate();
  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      // CR-5459: synthetic events ('approval:<sid>', 'contract:<cid>',
      // 'qbr:<eid>') deep-link to the system of record instead of the
      // generic event-detail modal — none of the metadata is editable
      // there anyway. Real CalendarEvent rows fall through to the
      // existing modal flow.
      const id = String(event.id ?? '');
      if (id.startsWith('approval:')) {
        // CR-5459 rework: the id is the accreditation; the supplier it
        // belongs to opens on its Approvals tab, where the expiry is held.
        if (typeof event.supplier === 'number') {
          navigate(`/suppliers/${event.supplier}?tab=approvals`);
        }
        return;
      }
      if (id.startsWith('contract:')) {
        // CR-5459 rework: /contracts is not a route — this sent every
        // Contract Renewal click to the dashboard. Contracts live on the
        // Purchasing page's Pricing tab; carry the id so that tab opens the
        // contract itself rather than just its list.
        // Only ever a numeric id — anything else would be a malformed
        // synthetic row, and concatenating it raw could inject query params.
        const contractId = id.slice('contract:'.length);
        navigate(/^\d+$/.test(contractId)
          ? `/purchasing?tab=contract_pricing&contract=${contractId}`
          : '/purchasing?tab=contract_pricing');
        return;
      }
      if (id.startsWith(AUDIT_ACTION_PREFIX)) {
        // Audit Lite: the action itself, highlighted under Questionnaires › Audits › Actions.
        navigate(auditActionPath(id));
        return;
      }
      if (id.startsWith('qbr:')) {
        // QBR rows ARE backed by a real CalendarEvent; strip the prefix
        // so the detail modal can load it normally.
        const realId = id.slice('qbr:'.length);
        const real = events.find(e => String(e.id) === realId);
        if (real) {
          eventModal.open(real);
          setEditForm(real);
          setIsEditing(false);
          return;
        }
      }
      eventModal.open(event);
      setEditForm(event);
      setIsEditing(false);
    },
    [eventModal, events, navigate]
  );

  const handleSaveEvent = async () => {
    if (!eventModal.data || !editForm) return;

    const eventId = eventModal.data.id;
    // CR-5459: synthetic events have string ids and no DB row; same guard
    // as handleCompleteEvent / handleDeleteEvent.
    if (typeof eventId !== 'number') return;
    try {
      const response = await calendarAPI.updateEvent(eventId, editForm as CalendarEvent);
      const updatedEvent = response.data;
      // Update local state with server response - this is the source of truth
      setEvents(prev => prev.map(e => e.id === eventId ? updatedEvent : e));
      eventModal.close();
      setIsEditing(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the event'));
    }
  };

  const handleCompleteEvent = async () => {
    if (!eventModal.data) return;

    const eventId = eventModal.data.id;
    // CR-5459: synthetic events have string ids ('approval:<sid>' etc.) and
    // no DB row to update — guard and noop. Real events are always numbers.
    if (typeof eventId !== 'number') return;
    try {
      const response = await calendarAPI.updateEvent(eventId, {
        ...eventModal.data,
        status: 'completed',
      });
      const updatedEvent = response.data;
      // Update local state with server response - this is the source of truth
      setEvents(prev => prev.map(e => e.id === eventId ? updatedEvent : e));
      eventModal.close();
    } catch (err) {
      console.error('Error completing event:', err);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventModal.data) return;

    const ok = await confirm({ title: 'Delete Event', description: 'Are you sure you want to delete this event? This action cannot be undone.', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    const deletedEventId = eventModal.data.id;
    // CR-5459: same guard as handleCompleteEvent — synthetic events are
    // read-only, only real CalendarEvent rows can be deleted.
    if (typeof deletedEventId !== 'number') return;
    try {
      await calendarAPI.deleteEvent(deletedEventId);
      // Remove from local state - server confirmed deletion
      setEvents(prev => prev.filter(e => e.id !== deletedEventId));
      eventModal.close();
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const handleOpenCreateModal = useCallback((startDate?: Date) => {
    const now = startDate || new Date();
    const endDate = new Date(now);
    endDate.setHours(endDate.getHours() + 1);

    setEditForm({
      title: '',
      description: '',
      event_type: 'meeting',
      start_datetime: now.toISOString(),
      end_datetime: endDate.toISOString(),
      location: '',
      all_day: false,
      status: 'scheduled',
    });
    setIsCreating(true);
    setCreateModalOpen(true);
  }, []);

  const handleSelectSlot = useCallback(
    (slotInfo: { start: Date; end: Date }) => {
      handleOpenCreateModal(slotInfo.start);
    },
    [handleOpenCreateModal]
  );

  const handleCreateEvent = async () => {
    if (!editForm.title) return;

    try {
      setIsCreating(true);
      const response = await calendarAPI.createEvent(editForm);
      const newEvent = response.data;
      // Add server response to local state - this is the source of truth
      setEvents(prev => [...prev, newEvent]);
      setCreateModalOpen(false);
      setIsCreating(false);
      setEditForm({});
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not create the event'));
      setIsCreating(false);
    }
  };

  // Calendar navigation functions
  const handleNavigate = useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, []);

  const handleNavigateToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleNavigatePrevious = useCallback(() => {
    const newDate = new Date(currentDate);
    if (calendarView === Views.MONTH) {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (calendarView === Views.WEEK) {
      newDate.setDate(newDate.getDate() - 7);
    } else if (calendarView === Views.DAY) {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  }, [currentDate, calendarView]);

  const handleNavigateNext = useCallback(() => {
    const newDate = new Date(currentDate);
    if (calendarView === Views.MONTH) {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (calendarView === Views.WEEK) {
      newDate.setDate(newDate.getDate() + 7);
    } else if (calendarView === Views.DAY) {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  }, [currentDate, calendarView]);

  // Filtered events
  const filteredEvents = useMemo(
    () => filterEvents(events, activeEventTypes, eventPeriod),
    [events, activeEventTypes, eventPeriod]
  );

  // CR-6435: Export — the events the panel currently shows, as CSV.
  const handleExportCsv = useCallback(() => {
    downloadCsv(eventsToCsv(filteredEvents), 'calendar-events.csv');
  }, [filteredEvents]);

  // Transform for calendar
  const calendarEvents = useMemo(
    () => transformEventsForCalendar(filteredEvents),
    [filteredEvents]
  );

  // Sorted events for list view
  const allListEvents = useMemo(() => {
    return filteredEvents.sort(
      (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
    );
  }, [filteredEvents]);

  // Pagination
  const totalPages = Math.ceil(allListEvents.length / ITEMS_PER_PAGE);

  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return allListEvents.slice(startIndex, endIndex);
  }, [allListEvents, currentPage]);

  // Group paginated events by month
  const paginatedEventsByMonth = useMemo(
    () => groupEventsByMonth(paginatedEvents),
    [paginatedEvents]
  );

  return {
    // View states
    viewMode,
    calendarView,
    currentDate,
    setViewMode,
    setCalendarView,

    // Data
    events,
    loading,
    error,
    filteredEvents,
    calendarEvents,
    allListEvents,
    paginatedEvents,
    paginatedEventsByMonth,

    // Filters
    activeEventTypes,
    eventPeriod,
    setEventPeriod,
    handleEventTypeToggle,
    filters,
    handleExportCsv,

    // Pagination
    currentPage,
    totalPages,
    setCurrentPage,

    // Modal states
    eventModal,
    isEditing,
    editForm,
    isCreating,
    createModalOpen,
    setIsEditing,
    setEditForm,
    setCreateModalOpen,

    // Actions
    fetchEvents,
    handleEventClick,
    handleSaveEvent,
    handleCompleteEvent,
    handleDeleteEvent,
    handleOpenCreateModal,
    handleSelectSlot,
    handleCreateEvent,

    // Navigation
    handleNavigate,
    handleNavigateToday,
    handleNavigatePrevious,
    handleNavigateNext,
  };
}
